"use client";

import { StopIcon } from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import WaveSurfer from "wavesurfer.js";

import { BigAudioPlayerSkeleton } from "@/components/skeletons/audio/BigAudioPlayerSkeleton";
import { SoftText } from "@/components/text/SoftText";
import { useAudioPlaying } from "@/lib/zustand/useAudioPlaying";
import { classNames } from "@/utils/classNames";
import { getTailwindConfig } from "@/utils/getTailwindConfig";
import { truncateFilename } from "@/utils/truncateFilename";
import { waveSurferCleanUp } from "@/utils/waveSurferCleanUp";

export function BigAudioPlayer({
  source,
  includePreviewText,
  bigWaves,
  filename,
  className,
}: {
  source: string;
  includePreviewText: boolean;
  bigWaves: boolean;
  filename: string | null;
  className?: string;
}) {
  // https://andreidobrinski.com/blog/implementing-an-audio-waveform-with-react-hooks-and-wavesurferjs/
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);

  const audioPlaying = useAudioPlaying((state) => state.audioPlaying);
  const setAudioPlaying = useAudioPlaying((state) => state.setAudioPlaying);
  const pauseAudio = useAudioPlaying((state) => state.pauseAudio);
  const setAudioPlayingToZero = useAudioPlaying(
    (state) => state.setAudioPlayingToZero
  );

  useEffect(() => {
    if (containerRef.current === null) {
      return;
    }

    const fullConfig = getTailwindConfig();
    const gray400 =
      (fullConfig.theme?.colors?.gray as { [key: string]: string })?.["400"] ??
      "#9CA3AF"; // Default to a mid gray if not found
    const gray900 =
      (fullConfig.theme?.colors?.gray as { [key: string]: string })?.["900"] ??
      "#111827"; // Default to a dark gray if not found

    const waveSurfer = WaveSurfer.create({
      container: containerRef.current,
      barGap: 2.5,
      barHeight: 0.8,
      barWidth: 1,
      cursorColor: "transparent",
      waveColor: gray400,
      progressColor: gray900,
      height: bigWaves ? 80 : 40,
      interact: false,
    });

    waveSurferRef.current = waveSurfer;

    waveSurfer.load(source);

    waveSurfer.on("ready", () => {
      setAudioLoaded(true);
    });

    waveSurfer.on("finish", () => {
      setIsPlaying(false);
      waveSurfer.options.interact = false;
      waveSurfer.seekTo(0);
    });

    return () => {
      waveSurferCleanUp(waveSurfer);
    };
  }, [source]);

  useEffect(() => {
    if (audioPlaying !== waveSurferRef.current && isPlaying) {
      setIsPlaying(false);
    }
  }, [audioPlaying, isPlaying]); // This effect runs whenever audioPlaying changes

  const togglePlayPause = () => {
    const currentWaveSurfer = waveSurferRef.current;

    if (currentWaveSurfer !== null) {
      if (isPlaying) {
        currentWaveSurfer.pause();
        setAudioPlayingToZero();
      } else {
        // if another audio is playing, pause it
        if (audioPlaying !== null) {
          pauseAudio();
          setAudioPlayingToZero();
        }
        // set current playing audio
        setAudioPlaying(currentWaveSurfer);

        currentWaveSurfer.play();
        currentWaveSurfer.options.interact = true;
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className={twMerge("flex items-center gap-x-2", className)}>
      {audioLoaded && (
        <button
          onClick={(e) => {
            // Prevent the click from bubbling up to the parent div if it's inside a button
            e.stopPropagation();
            togglePlayPause();
          }}
          type="button"
          className="flex shrink-0 flex-col items-center p-1 transition-all hover:scale-110 hover:opacity-80"
        >
          {isPlaying ?
            <StopIcon className="h-6 w-6 shrink-0 fill-gray-900" />
          : <PlayIcon className="h-6 w-6 shrink-0 fill-gray-900" />}
          {includePreviewText && <span className="text-xs">Preview</span>}
        </button>
      )}

      {filename !== null && (
        <SoftText
          elementType="span"
          className="min-w-0 max-w-[110px] shrink overflow-hidden whitespace-nowrap"
        >
          {truncateFilename(filename)}
        </SoftText>
      )}

      {!audioLoaded && <BigAudioPlayerSkeleton bigWaves={bigWaves} />}

      {/* The click handler only stops bubbling while seeking; it is not a control, so the element is presentational */}
      <div
        ref={containerRef}
        role="presentation"
        className={classNames(
          bigWaves ? "h-[80px]" : "h-[40px]",
          "flex-1",
          audioLoaded ? "block" : "hidden"
        )}
        onClick={(e) => {
          // Prevent the click from bubbling up to the parent element if it's inside a button
          if (isPlaying) {
            e.stopPropagation();
          }
        }}
      />
    </div>
  );
}
